import { escapeXml, buildXmlPayload, sendXmlRequest } from './core';

// ============ CONTRACT LINE ENTRY CONFIGURATION ============
const CONTRACT_LINE_ENTRY_TYPES = {
  'PROJECTCONTRACTLINEENTRY': {
    parentObject: 'PROJECTCONTRACTLINE',
    parentKeyFields: ['PROJECTCONTRACTLINEID', 'PROJECTCONTRACTLINE.RECORDNO'],
    containerTag: 'PROJECTCONTRACTLINEENTRIES',
    entryTag: 'PROJECTCONTRACTLINEENTRY',
    entryRecordNoField: 'RECORDNO',
  }
};
const CONTRACT_LINE_ENTRY_EXCLUDED_FIELDS = ['WHENCREATED', 'WHENMODIFIED', 'CREATEDBY', 'MODIFIEDBY'];

const shouldExcludeFromContractLineEntry = (key, parentKeyHeader) => {
  const upperKey = key.toUpperCase();
  if (CONTRACT_LINE_ENTRY_EXCLUDED_FIELDS.includes(upperKey)) return true;
  if (upperKey === parentKeyHeader.toUpperCase()) return true;
  return false;
};

// ============ DETECTION ============
export const isContractLineEntryType = (objectName) => {
  return Object.keys(CONTRACT_LINE_ENTRY_TYPES).includes(objectName?.toUpperCase());
};

export const getContractLineEntryConfig = (objectName) => {
  return CONTRACT_LINE_ENTRY_TYPES[objectName?.toUpperCase()] || null;
};

export const detectContractLineParentKey = (headers) => {
  const cfg = CONTRACT_LINE_ENTRY_TYPES['PROJECTCONTRACTLINEENTRY'];
  if (!cfg) return null;
  const upperHeaders = headers.map(h => String(h).toUpperCase().trim());
  for (const keyField of cfg.parentKeyFields) {
    if (upperHeaders.includes(keyField.toUpperCase())) return keyField.toUpperCase();
  }
  return null;
};

// ============ FUNCTIONS ============
export const getContractLineUpdateSummary = (objectName, records, parentKeyHeader) => {
  const cfg = CONTRACT_LINE_ENTRY_TYPES[objectName?.toUpperCase()];
  if (!cfg) return null;
  
  const grouped = {};
  const missingParentKey = [];
  let newEntryCount = 0;
  let updateEntryCount = 0;
  
  records.forEach((rec, idx) => {
    const parentVal = rec[parentKeyHeader];
    if (!parentVal) { missingParentKey.push(idx + 2); return; }
    if (!grouped[parentVal]) grouped[parentVal] = { updates: 0, creates: 0 };
    const entryRecordNo = rec[cfg.entryRecordNoField];
    if (entryRecordNo !== undefined && entryRecordNo !== '' && entryRecordNo !== null) {
      grouped[parentVal].updates++;
      updateEntryCount++;
    } else {
      grouped[parentVal].creates++;
      newEntryCount++;
    }
  });
  
  return {
    config: cfg,
    objectName: objectName.toUpperCase(),
    parentKeyHeader,
    parentLines: Object.entries(grouped).map(([id, counts]) => ({ 
      id, updateCount: counts.updates, createCount: counts.creates, totalCount: counts.updates + counts.creates 
    })),
    missingParentKeyRows: missingParentKey,
    totalEntries: records.length,
    totalParentLines: Object.keys(grouped).length,
    updateEntryCount,
    newEntryCount
  };
};

export const buildContractLinePreviewXml = (objectName, records, parentKeyHeader) => {
  const cfg = CONTRACT_LINE_ENTRY_TYPES[objectName?.toUpperCase()];
  if (!cfg) return '';
  
  const grouped = {};
  records.forEach(rec => {
    const parentVal = rec[parentKeyHeader];
    if (!parentVal) return;
    if (!grouped[parentVal]) grouped[parentVal] = [];
    grouped[parentVal].push(rec);
  });
  
  const parentIds = Object.keys(grouped);
  if (parentIds.length === 0) return `<!-- No valid records with ${parentKeyHeader} -->`;
  
  const firstId = parentIds[0];
  const firstGroupRecords = grouped[firstId];
  const parentKeyXmlTag = parentKeyHeader === 'PROJECTCONTRACTLINE.RECORDNO' ? 'RECORDNO' : parentKeyHeader;
  
  const sampleEntries = firstGroupRecords.slice(0, 2);
  const entriesXml = sampleEntries.map(entry => {
    const entryFields = Object.entries(entry)
      .filter(([k, v]) => {
        if (shouldExcludeFromContractLineEntry(k, parentKeyHeader)) return false;
        if (k.toUpperCase() === cfg.entryRecordNoField && (v === '' || v === undefined || v === null)) return false;
        if (v === '' || v === undefined || v === null) return false;
        return true;
      })
      .map(([k, v]) => `<${k}>${escapeXml(String(v))}</${k}>`)
      .join('\n          ');
    const isNew = !entry[cfg.entryRecordNoField] || entry[cfg.entryRecordNoField] === '';
    const comment = isNew ? ' <!-- NEW ENTRY -->' : '';
    return `<${cfg.entryTag}>${comment}\n          ${entryFields}\n        </${cfg.entryTag}>`;
  }).join('\n        ');
  
  const moreEntriesNote = firstGroupRecords.length > 2 ? `\n        <!-- ... and ${firstGroupRecords.length - 2} more entries ... -->` : '';
  const moreParentsNote = parentIds.length > 1 ? `\n\n<!-- ... and ${parentIds.length - 1} more contract line(s) will be updated ... -->` : '';
  
  return `<function controlid="${escapeXml(firstId)}">
  <update>
    <${cfg.parentObject}>
      <${parentKeyXmlTag}>${escapeXml(firstId)}</${parentKeyXmlTag}>
      <${cfg.containerTag}>
        ${entriesXml}${moreEntriesNote}
      </${cfg.containerTag}>
    </${cfg.parentObject}>
  </update>
</function>${moreParentsNote}`;
};

export const writeContractLineEntries = async (config, objectName, records, parentKeyHeader) => {
  const cfg = CONTRACT_LINE_ENTRY_TYPES[objectName?.toUpperCase()];
  if (!cfg) throw new Error(`Unknown contract line entry type: ${objectName}`);
  
  const missingKey = records.filter(r => !r[parentKeyHeader]);
  if (missingKey.length > 0) throw new Error(`${missingKey.length} record(s) missing ${parentKeyHeader}.`);
  
  const grouped = {};
  records.forEach(rec => {
    const parentVal = rec[parentKeyHeader];
    if (!grouped[parentVal]) grouped[parentVal] = [];
    grouped[parentVal].push(rec);
  });
  
  const parentKeyXmlTag = parentKeyHeader === 'PROJECTCONTRACTLINE.RECORDNO' ? 'RECORDNO' : parentKeyHeader;
  
  const functions = Object.entries(grouped).map(([parentVal, entries]) => {
    const entriesXml = entries.map(entry => {
      const entryFields = {};
      Object.entries(entry).forEach(([key, val]) => {
        if (shouldExcludeFromContractLineEntry(key, parentKeyHeader)) return;
        if (key.toUpperCase() === cfg.entryRecordNoField && (val === '' || val === undefined || val === null)) return;
        if (val === '' || val === undefined || val === null) return;
        entryFields[key] = val;
      });
      const fieldsXml = Object.entries(entryFields).map(([k, v]) => `<${k}>${escapeXml(String(v))}</${k}>`).join('');
      return `<${cfg.entryTag}>${fieldsXml}</${cfg.entryTag}>`;
    }).join('');
    
    return `<function controlid="${escapeXml(String(parentVal))}"><update><${cfg.parentObject}><${parentKeyXmlTag}>${escapeXml(String(parentVal))}</${parentKeyXmlTag}><${cfg.containerTag}>${entriesXml}</${cfg.containerTag}></${cfg.parentObject}></update></function>`;
  }).join('');
  
  const xmlPayload = buildXmlPayload(config, functions);
  const result = await sendXmlRequest(config, cfg.parentObject, xmlPayload, 'CONTRACT LINE ENTRY UPDATE');
  return result.results;
};
